// App-level actions: persistence, import/export, clipboard, navigation.

import { flushSync } from 'react-dom';
import * as E from '@simplenotation/core';
import type { Score } from '@simplenotation/core';
import { api, ApiError } from '../api/client';
import { getState, setState, toast, loadScore, markSaved, edit, unsaved, openModal } from '../state/store';
import { saveJson, loadJson, removeKey, DRAFT_KEY } from '../state/persist';
import type { CmdCtx } from '../commands/types';
import { downloadBytes, pickFile } from '../io/download';
import { ACCEPT_ALL, parseFile } from '../io/parse';
import { track, fileFormat } from '../analytics';

// ---------------------------------------------------------------- routing

export function navigate(path: string, replace = false): void {
  if (location.pathname === path) return;
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ---------------------------------------------------------------- command search focus

let focusSearch: (() => void) | null = null;
export function registerSearchFocus(fn: (() => void) | null): void {
  focusSearch = fn;
}

/** Open the command search and focus its input in the same tick (T12). */
export function openSearch(): void {
  flushSync(() => setState({ searchOpen: true, settingsOpen: false, metronomeOpen: false }));
  focusSearch?.();
}

// ---------------------------------------------------------------- session

export async function ensureUser(): Promise<boolean> {
  if (getState().user) return true;
  try {
    const { user } = await api.guest();
    setState({ user });
    return true;
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Could not start a session.', 'error');
    return false;
  }
}

export async function refreshMe(): Promise<void> {
  try {
    const { user } = await api.me();
    setState({ user });
  } catch {
    /* offline: keep working locally */
  }
}

// ---------------------------------------------------------------- local draft (unsaved, no slug)


export function saveDraft(): void {
  const s = getState();
  if (s.doc.slug) return;
  saveJson(DRAFT_KEY, { score: s.ed.score, at: Date.now() });
}

export function loadDraft(): Score | null {
  const d = loadJson<{ score?: unknown } | null>(DRAFT_KEY, null);
  if (!d?.score) return null;
  const v = E.validateScore(d.score);
  return v.ok ? v.score : null;
}

export function clearDraft(): void {
  removeKey(DRAFT_KEY);
}

// ---------------------------------------------------------------- open / save

export async function openScore(slug: string, version?: number): Promise<void> {
  setState((s) => ({ doc: { ...s.doc, status: 'loading', slug } }));
  try {
    const [{ meta }, { notation, version: v }] = await Promise.all([api.getMeta(slug), api.getNotation(slug, version)]);
    const canEdit = meta.canEdit && (!version || version === meta.latestVersion);
    loadScore(notation, { slug, version: meta.latestVersion, privacy: meta.privacy, canEdit, ownerName: meta.owner.name, status: 'idle' });
    if (version && version !== meta.latestVersion) toast(`Viewing version ${v} (read-only). Restore it from Versions to edit.`);
    // Set the mode both ways: a score you can edit (such as a copy you just
    // made of someone else's) must not stay in the view mode of the last one.
    setState({ mode: canEdit ? 'edit' : 'view' });
    // The server already set a descriptive title for shared scores; keep it.
    const title = notation.meta.title || 'Untitled';
    if (!document.title.startsWith(`${title} · `)) document.title = `${title} · SimpleNotation`;
  } catch (e) {
    setState((s) => ({ doc: { ...s.doc, status: 'error', error: e instanceof ApiError ? e.message : String(e) } }));
    toast(e instanceof ApiError && e.status === 404 ? 'That score doesn’t exist or is private.' : `Couldn’t open the score: ${(e as Error).message}`, 'error');
  }
}

let saving: Promise<void> | null = null;

export async function save(): Promise<void> {
  if (saving) return saving;
  saving = doSave().finally(() => {
    saving = null;
  });
  return saving;
}

async function doSave(): Promise<void> {
  const s = getState();
  if (!s.doc.canEdit) {
    await duplicateScore();
    return;
  }
  if (!(await ensureUser())) return;
  const score = getState().ed.score;
  const historyIndex = getState().history.index;
  setState((st) => ({ doc: { ...st.doc, status: 'saving' } }));
  try {
    if (!s.doc.slug) {
      const { meta } = await api.createScore(score.meta.title || 'Untitled', score);
      clearDraft();
      setState((st) => ({ doc: { ...st.doc, slug: meta.slug, privacy: meta.privacy, canEdit: true, ownerName: meta.owner.name } }));
      markSavedAt(meta.latestVersion, historyIndex, meta.slug);
      navigate(`/s/${meta.slug}`, true);
      track('create_score', { tracks: score.tracks.length, bars: score.bars.length });
      toast('Saved. Only you can see this score until you share it.', 'success');
    } else {
      const { version } = await api.putNotation(s.doc.slug, s.doc.version ?? 1, score);
      markSavedAt(version, historyIndex);
    }
    document.title = `${score.meta.title || 'Untitled'} · SimpleNotation`;
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      setState((st) => ({ doc: { ...st.doc, status: 'conflict', error: 'A newer version was saved elsewhere.' } }));
      toast('Someone saved a newer version. Open Versions to compare, or duplicate your copy.', 'error', 8000);
      return;
    }
    setState((st) => ({ doc: { ...st.doc, status: 'error', error: (e as Error).message } }));
    toast(`Save failed: ${(e as Error).message}`, 'error');
  }
}

function markSavedAt(version: number, historyIndex: number, slug?: string): void {
  // If the user kept editing while the save was in flight, the saved point is the index at send time.
  markSaved(version, slug);
  setState((s) => ({ history: { ...s.history, savedIndex: historyIndex <= s.history.index ? historyIndex : s.history.savedIndex } }));
}

export async function duplicateScore(): Promise<void> {
  if (!(await ensureUser())) return;
  const s = getState();
  try {
    let slug: string;
    if (s.doc.slug && unsaved() === 0) {
      ({ meta: { slug } } = await api.fork(s.doc.slug));
    } else {
      const score = { ...s.ed.score, meta: { ...s.ed.score.meta, title: `${s.ed.score.meta.title || 'Untitled'} (copy)` } };
      ({ meta: { slug } } = await api.createScore(score.meta.title, score));
    }
    navigate(`/s/${slug}`);
    track('duplicate_score', { own: s.doc.canEdit });
    toast('Duplicated. You can edit this copy.', 'success');
  } catch (e) {
    toast(`Couldn’t duplicate: ${(e as Error).message}`, 'error');
  }
}

export function newScore(): void {
  if (unsaved() > 0 && !confirm('Discard unsaved changes and start a new score?')) return;
  clearDraft();
  loadScore(E.createScore({ title: 'Untitled', bars: 4, instrument: 'classical-guitar' }));
  setState({ mode: 'edit' });
  navigate('/new');
  track('new_score');
}

export async function setPrivacy(privacy: 'private' | 'unlisted' | 'public'): Promise<void> {
  const s = getState();
  if (!s.doc.slug) {
    await save();
  }
  const slug = getState().doc.slug;
  if (!slug) return;
  try {
    const { meta } = await api.patchMeta(slug, { privacy });
    setState((st) => ({ doc: { ...st.doc, privacy: meta.privacy } }));
    track(privacy === 'private' ? 'make_private' : 'share', { method: privacy, content_type: 'score' });
    toast(privacy === 'private' ? 'Only you can see this score.' : 'Anyone with the link can view this score.', 'success');
  } catch (e) {
    toast(`Couldn’t change sharing: ${(e as Error).message}`, 'error');
  }
}

// ---------------------------------------------------------------- import / export

export async function importFile(opts: { asTrack?: boolean; file?: File } = {}): Promise<void> {
  const file = opts.file ?? (await pickFile(ACCEPT_ALL));
  if (!file) return;
  try {
    const score = await parseFile(file);
    const v = E.validateScore(score);
    if (!v.ok) throw new Error(v.error);
    if (opts.asTrack) {
      edit((st) => score.tracks.reduce((acc, t) => E.appendTrackFrom(acc, t), st), { label: 'Import instruments' });
      track('import_score', { format: fileFormat(file.name), as_track: true, source: 'editor' });
      toast(`Added ${score.tracks.length} instrument${score.tracks.length === 1 ? '' : 's'} from ${file.name}.`, 'success');
      return;
    }
    if (unsaved() > 0 && !confirm('Replace the current score with the imported file? Unsaved changes will be lost.')) return;
    loadScore(score);
    setState({ mode: 'edit' });
    navigate('/new');
    track('import_score', { format: fileFormat(file.name), as_track: false, source: 'editor' });
    toast(`Imported ${file.name}. Save to keep it.`, 'success');
  } catch (e) {
    console.error(e);
    track('import_failed', { format: fileFormat(file.name), source: 'editor' });
    toast(`Import failed: ${(e as Error).message}`, 'error', 7000);
  }
}

function fileBase(): string {
  const title = getState().ed.score.meta.title || 'Untitled';
  return title.replace(/[^\p{L}\p{N} _.-]+/gu, '').trim() || 'score';
}

export async function exportMusicXml(): Promise<void> {
  try {
    downloadBytes(E.exportMusicXml(getState().ed.score), `${fileBase()}.musicxml`, 'application/vnd.recordare.musicxml+xml');
    track('export_score', { format: 'musicxml' });
  } catch (e) {
    toast(`Export failed: ${(e as Error).message}`, 'error');
  }
}

export async function exportMidi(): Promise<void> {
  try {
    downloadBytes(E.exportMidi(getState().ed.score), `${fileBase()}.mid`, 'audio/midi');
    track('export_score', { format: 'midi' });
  } catch (e) {
    toast(`Export failed: ${(e as Error).message}`, 'error');
  }
}

export function exportTextTab(): void {
  try {
    downloadBytes(E.exportTextTab(getState().ed.score), `${fileBase()}.txt`, 'text/plain;charset=utf-8');
    track('export_score', { format: 'text_tab' });
  } catch (e) {
    toast((e as Error).message, 'error');
  }
}

export async function copyTextTab(): Promise<void> {
  try {
    await navigator.clipboard.writeText(E.exportTextTab(getState().ed.score));
    track('export_score', { format: 'text_tab_copy' });
    toast('Text tab copied. Paste it anywhere.', 'success');
  } catch (e) {
    toast(e instanceof Error && e.name !== 'NotAllowedError' ? e.message : 'Could not copy to the clipboard', 'error');
  }
}

export async function exportGuitarPro(): Promise<void> {
  try {
    toast('Preparing Guitar Pro file…');
    const { exportGuitarPro: gp } = await import('../io/gp-export');
    const { bytes, warnings } = await gp(getState().ed.score);
    downloadBytes(bytes, `${fileBase()}.gp`, 'application/octet-stream');
    track('export_score', { format: 'gp' });
    for (const w of warnings) toast(w, 'info', 6000);
  } catch (e) {
    console.error(e);
    toast(`Guitar Pro export failed: ${(e as Error).message}`, 'error');
  }
}

let audioExportRunning = false;

export async function exportAudio(format: 'wav' | 'mp3'): Promise<void> {
  if (audioExportRunning) return toast('An audio export is already running');
  audioExportRunning = true;
  try {
    toast(format === 'mp3' ? 'Rendering audio, then encoding MP3…' : 'Rendering audio…', 'info', 6000);
    const { exportAudio: render } = await import('../io/audioExport');
    const s = getState();
    const bytes = await render(s.ed.score, s.timeline, format);
    downloadBytes(bytes, `${fileBase()}.${format}`, format === 'mp3' ? 'audio/mpeg' : 'audio/wav');
    track('export_score', { format });
  } catch (e) {
    console.error(e);
    toast(`Audio export failed: ${(e as Error).message}`, 'error');
  } finally {
    audioExportRunning = false;
  }
}

export async function exportPng(): Promise<void> {
  try {
    toast('Preparing image…');
    const { renderPng } = await import('../io/pdfExport');
    const { bytes, kind } = await renderPng(getState().ed.score, getState().view);
    if (kind === 'png') downloadBytes(bytes, `${fileBase()}.png`, 'image/png');
    else {
      downloadBytes(bytes, `${fileBase()}-pages.zip`, 'application/zip');
      toast('The score is long, so each page is a separate PNG in a .zip file.', 'info', 6000);
    }
    track('export_score', { format: 'png', zip: kind !== 'png' });
  } catch (e) {
    console.error(e);
    toast(`Image export failed: ${(e as Error).message}`, 'error');
  }
}

export async function exportPdf(): Promise<void> {
  try {
    toast('Preparing PDF…');
    const { renderPdf } = await import('../io/pdfExport');
    const bytes = await renderPdf(getState().ed.score, getState().view);
    downloadBytes(bytes, `${fileBase()}.pdf`, 'application/pdf');
    track('export_score', { format: 'pdf' });
  } catch (e) {
    console.error(e);
    toast(`PDF export failed: ${(e as Error).message}`, 'error');
  }
}

// ---------------------------------------------------------------- clipboard

export function copy(ctx: CmdCtx): void {
  const data = E.copySelection(ctx.ed);
  if (!data) return;
  setState({ clipboard: data });
  try {
    void navigator.clipboard?.writeText(JSON.stringify(data));
  } catch {
    /* clipboard permission denied: internal clipboard still works */
  }
  toast('Copied');
}

export function cut(ctx: CmdCtx): void {
  const res = E.cutSelection(ctx.ed);
  if (!res.data) return;
  setState({ clipboard: res.data });
  edit(() => res.state, { label: 'Cut' });
}

export async function paste(ctx: CmdCtx): Promise<void> {
  let data = getState().clipboard;
  try {
    const text = await navigator.clipboard?.readText();
    if (text) {
      const parsed = JSON.parse(text);
      if (E.isClipboardData(parsed)) data = parsed;
    }
  } catch {
    /* fall back to the internal clipboard */
  }
  if (!data) {
    toast('Nothing to paste.');
    return;
  }
  const d = data;
  void ctx;
  edit((e) => E.paste(e, d), { label: 'Paste' });
}

export { openModal };
